/**
 * The organisation-creation form (platform design §18.2): creates the tenant and makes the
 * owner its administrator, with opening credits and a ledger note optional
 * but paired — the API refuses opening credits with no note
 * (`core balances._require_note`) — so this dialog refuses the same pairing
 * before it ever calls the API, and reuses the note's own hint as the
 * refusal.
 */

import { useState, type FormEvent, type ReactNode } from 'react';

import { defineCatalogue } from '../../i18n';
import { FormDialog, fieldControlProps, fieldErrorOf } from '../../chrome/form';
import { Hint } from '../../chrome/hint';
import type { ApiFailure } from '../../lib/http/errors';
import { Button } from '../../ui/button';
import { Field, FieldError, FieldLabel } from '../../ui/field';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { useCreateTenant } from '../api/queries';

const catalogue = defineCatalogue(
  {
    title: 'Criar uma organização',
    description:
      'Cria a organização e torna o proprietário seu administrador. Opcionalmente, adiciona créditos iniciais registrados como uma concessão.',
    'name.label': 'Nome da organização',
    'name.hint': 'Este será o nome exibido para todos os membros desta organização.',
    'ownerEmail.label': 'E-mail do proprietário',
    'ownerEmail.hint':
      'Concederá privilégios administrativos a esta pessoa, que precisa já ter feito login antes.',
    'initialCredits.label': 'Créditos iniciais',
    'initialCredits.hint':
      'Fornecerá um saldo inicial de créditos; deixe em 0 para adicionar depois pela página da organização.',
    'note.label': 'Observação',
    'note.hint': 'Obrigatório ao adicionar créditos iniciais: o motivo, para o livro-razão.',
    ownerNotSignedIn:
      'Esta pessoa precisa fazer login pelo menos uma vez antes de poder possuir uma organização.',
    ownerAmbiguous:
      'Várias contas compartilham este e-mail; pergunte ao proprietário qual delas ele usa.',
    submit: 'Criar',
    cancel: 'Cancelar',
  },
  {
    title: 'Create an organisation',
    description:
      'Creates the organisation and makes the owner its administrator. Optionally adds opening credits recorded as a grant.',
    'name.label': 'Organisation name',
    'name.hint': 'This will be the name displayed to all members of this organisation.',
    'ownerEmail.label': "Owner's email",
    'ownerEmail.hint':
      'Will grant administrative privileges to this person, who must have signed in once before.',
    'initialCredits.label': 'Opening credits',
    'initialCredits.hint':
      "Will provide a starting credit balance; leave at 0 to add credits later from the organisation's page.",
    'note.label': 'Note',
    'note.hint': 'Required when adding opening credits: the reason, for the ledger.',
    ownerNotSignedIn: 'This person must sign in at least once before they can own an organisation.',
    ownerAmbiguous: 'Several accounts share this email; ask the owner which one they use.',
    submit: 'Create',
    cancel: 'Cancel',
  },
);
const t = catalogue.t;

const NAME_ID = 'create-organisation-name';
const OWNER_EMAIL_ID = 'create-organisation-owner-email';
const INITIAL_CREDITS_ID = 'create-organisation-initial-credits';
const NOTE_ID = 'create-organisation-note';
const NAME_ERROR_ID = `${NAME_ID}-error`;
const OWNER_EMAIL_ERROR_ID = `${OWNER_EMAIL_ID}-error`;
const NOTE_ERROR_ID = `${NOTE_ID}-error`;

/** A 404 here carries no message at all and a 409 is ambiguity, not
 *  something a tenant reads as "conflict" — both are replaced with the
 *  sentence of their own rather than `ErrorState`'s generic text. */
function dialogFailure(failure: ApiFailure): ApiFailure {
  if (failure.kind === 'not-found') {
    return { kind: 'invalid', field: null, message: t('ownerNotSignedIn') };
  }
  if (failure.kind === 'conflict') {
    return { kind: 'invalid', field: null, message: t('ownerAmbiguous') };
  }
  return failure;
}

export function CreateOrganisationDialog(props: {
  triggerLabel: string;
  onCreated: (tenantId: string) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [initialCredits, setInitialCredits] = useState(0);
  const [note, setNote] = useState('');
  const [noteRequired, setNoteRequired] = useState(false);
  const mutation = useCreateTenant();

  function reset(): void {
    setName('');
    setOwnerEmail('');
    setInitialCredits(0);
    setNote('');
    setNoteRequired(false);
    mutation.reset();
  }

  function handleOpenChange(next: boolean): void {
    setOpen(next);
    if (!next) reset();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void | Promise<unknown> {
    event.preventDefault();
    if (initialCredits > 0 && note.trim() === '') {
      setNoteRequired(true);
      return;
    }
    return mutation
      .mutateAsync({
        name,
        owner_email: ownerEmail,
        initial_credits: initialCredits,
        note: note.trim() === '' ? null : note,
      })
      .then((created) => props.onCreated(created.tenant_id));
  }

  const serverFailure = mutation.error ? dialogFailure(mutation.error.failure) : null;
  const failure: ApiFailure | null = noteRequired
    ? { kind: 'invalid', field: 'note', message: t('note.hint') }
    : serverFailure;

  return (
    <FormDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={t('title')}
      description={t('description')}
      submitLabel={t('submit')}
      cancelLabel={t('cancel')}
      pending={mutation.isPending}
      failure={failure}
      onSubmit={handleSubmit}
      trigger={<Button type="button">{props.triggerLabel}</Button>}
    >
      <Field data-invalid={fieldErrorOf(failure, 'name') !== null || undefined}>
        <div className="flex items-center gap-2">
          <FieldLabel htmlFor={NAME_ID}>{t('name.label')}</FieldLabel>
          <Hint label={t('name.label')}>{t('name.hint')}</Hint>
        </div>
        <Input
          id={NAME_ID}
          name="name"
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
          {...fieldControlProps(failure, 'name', NAME_ERROR_ID)}
        />
        <FieldError id={NAME_ERROR_ID}>{fieldErrorOf(failure, 'name')}</FieldError>
      </Field>
      <Field data-invalid={fieldErrorOf(failure, 'owner_email') !== null || undefined}>
        <div className="flex items-center gap-2">
          <FieldLabel htmlFor={OWNER_EMAIL_ID}>{t('ownerEmail.label')}</FieldLabel>
          <Hint label={t('ownerEmail.label')}>{t('ownerEmail.hint')}</Hint>
        </div>
        <Input
          id={OWNER_EMAIL_ID}
          name="owner_email"
          type="email"
          required
          value={ownerEmail}
          onChange={(event) => setOwnerEmail(event.target.value)}
          {...fieldControlProps(failure, 'owner_email', OWNER_EMAIL_ERROR_ID)}
        />
        <FieldError id={OWNER_EMAIL_ERROR_ID}>{fieldErrorOf(failure, 'owner_email')}</FieldError>
      </Field>
      <Field>
        <div className="flex items-center gap-2">
          <FieldLabel htmlFor={INITIAL_CREDITS_ID}>{t('initialCredits.label')}</FieldLabel>
          <Hint label={t('initialCredits.label')}>{t('initialCredits.hint')}</Hint>
        </div>
        <Input
          id={INITIAL_CREDITS_ID}
          name="initial_credits"
          type="number"
          min={0}
          step={1}
          value={initialCredits}
          onChange={(event) => {
            const next = Number(event.target.value);
            setInitialCredits(next);
            if (next <= 0) setNoteRequired(false);
          }}
        />
      </Field>
      <Field data-invalid={fieldErrorOf(failure, 'note') !== null || undefined}>
        <div className="flex items-center gap-2">
          <FieldLabel htmlFor={NOTE_ID}>{t('note.label')}</FieldLabel>
          <Hint label={t('note.label')}>{t('note.hint')}</Hint>
        </div>
        <Textarea
          id={NOTE_ID}
          name="note"
          value={note}
          onChange={(event) => {
            setNote(event.target.value);
            if (event.target.value.trim() !== '') setNoteRequired(false);
          }}
          {...fieldControlProps(failure, 'note', NOTE_ERROR_ID)}
        />
        <FieldError id={NOTE_ERROR_ID}>{fieldErrorOf(failure, 'note')}</FieldError>
      </Field>
    </FormDialog>
  );
}
